import { useNavigate } from "react-router-dom";
import PageMeta from "@/components/PageMeta";

/**
 * Public "About" page — sister to the FAQ page in serving both humans
 * and AEO crawlers. Light-touch content: who built the product, what
 * problem it solves, who it's for, and how to engage further.
 *
 * Content drafted from the live product surface (pricing config,
 * customer logos rendered on the login page, feature list) as of
 * Feb 2026. Designed to be fully self-contained — no login required,
 * no external API calls.
 */
export default function AboutPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#f4f4f4] font-['Figtree']" data-testid="about-page">
      <PageMeta
        title="About · Slides by Wokelo — AI for consulting-grade PowerPoint decks"
        description="About Slides by Wokelo: an AI-powered presentation generator built for consultants, investment bankers, PE associates, and M&A deal teams. Built by Wokelo AI."
      />

      <header className="w-full px-6 sm:px-12 py-5 flex items-center justify-between">
        <button
          onClick={() => navigate("/")}
          className="flex items-center gap-2 hover:opacity-80 transition-opacity"
          data-testid="brand-home-link"
        >
          <img src="/landing/wokelo_logo.svg" alt="Wokelo" className="w-[110px] h-auto" />
        </button>
        <button
          onClick={() => navigate("/login")}
          className="text-[14px] font-semibold text-teal-600 hover:text-teal-700"
          data-testid="header-login-link"
        >
          Log in →
        </button>
      </header>

      <main className="max-w-[760px] mx-auto px-6 py-12">
        <h1 className="text-[40px] leading-tight font-extrabold text-gray-900 tracking-tight mb-3" data-testid="about-heading">
          About Slides by Wokelo
        </h1>
        <p className="text-[16px] text-gray-600 leading-relaxed mb-10">
          AI for consulting-grade PowerPoint decks. Built for the teams that
          live in slides every day — consultants, investment bankers, private
          equity associates, corporate strategy leads, and M&A deal teams.
        </p>

        <section className="space-y-8 text-[15px] text-gray-700 leading-relaxed">
          <div>
            <h2 className="text-[20px] font-bold text-gray-900 mb-2">The problem</h2>
            <p>
              The first draft of a strategy deck, a due-diligence pack, or a
              pitch book is a slow, manual exercise — even for senior teams.
              Hours disappear into formatting, sourcing, and the visual
              translation of analysis into the "title-on-top, supporting
              visuals below" convention that elite firms expect.
            </p>
          </div>

          <div>
            <h2 className="text-[20px] font-bold text-gray-900 mb-2">What Slides by Wokelo does</h2>
            <p>
              Slides by Wokelo turns a topic, a set of source documents, or a
              research query into fully formatted PowerPoint output. The
              styling matches the conventions used at KPMG, Deloitte, Bain,
              McKinsey, BCG, and Alvarez & Marsal: governing message on top,
              structured layout, consistent typography, citations inline.
              Downloads are real .pptx files — open them in PowerPoint or
              Google Slides and continue editing as you would any other deck.
            </p>
          </div>

          <div>
            <h2 className="text-[20px] font-bold text-gray-900 mb-2">Use cases</h2>
            <ul className="list-disc pl-5 space-y-1.5">
              <li><strong>Investor and board decks</strong> — turn a memo into a 5–25 slide deck.</li>
              <li><strong>Due diligence packs</strong> — synthesize market sizing, competitive landscape, and financial overviews from uploaded PDFs.</li>
              <li><strong>Pitch books and teasers</strong> — sell-side and buy-side mandates.</li>
              <li><strong>Market research</strong> — single-slide outputs from a research query.</li>
              <li><strong>Internal updates</strong> — quick polished slides from notes or meeting transcripts.</li>
            </ul>
          </div>

          <div>
            <h2 className="text-[20px] font-bold text-gray-900 mb-2">Built on best-in-class AI</h2>
            <p>
              Powered by Wokelo's Research APIs, Wokelo's PPT Skill and
              Private LLM. On Pro / Enterprise — a premium data add-on
              covering S&P Capital IQ, Crunchbase, LinkedIn, SimilarWeb
              and several other alternative datasets covering patents,
              reviews, podcast transcripts etc. Enterprise customers can
              run the entire stack on-prem or in a private VPC with a
              fully local private LLM if their data classification
              requires it.
            </p>
          </div>

          <div>
            <h2 className="text-[20px] font-bold text-gray-900 mb-2">Data privacy</h2>
            <p>
              <strong>Your content is never used to train any AI model.</strong>
              Not on Trial, not on Starter, not on Pro, not on Enterprise.
              This is a hard product guarantee, not a setting you opt out of.
              For teams with stricter requirements, Enterprise offers on-prem
              and VPC deployment with private-LLM inference inside your own
              network.
            </p>
          </div>

          <div>
            <h2 className="text-[20px] font-bold text-gray-900 mb-2">Built by Wokelo AI</h2>
            <p>
              Slides by Wokelo is built by{" "}
              <a href="https://www.wokelo.ai" target="_blank" rel="noopener noreferrer" className="text-teal-600 hover:text-teal-700 underline underline-offset-2">
                Wokelo AI
              </a>
              . For partnership, enterprise demos, or media enquiries reach
              out at{" "}
              <a href="mailto:sid@wokelo.ai" className="text-teal-600 hover:text-teal-700 underline underline-offset-2">
                sid@wokelo.ai
              </a>
              .
            </p>
          </div>
        </section>

        <div className="mt-12 flex flex-wrap gap-3">
          <button
            onClick={() => navigate("/signup")}
            className="h-11 px-5 rounded-xl bg-gray-900 hover:bg-black text-white font-semibold text-[14px] shadow-sm hover:shadow-md transition-all"
            data-testid="about-signup-cta"
          >
            Start free trial
          </button>
          <button
            onClick={() => navigate("/faq")}
            className="h-11 px-5 rounded-xl border border-gray-200 bg-white hover:bg-gray-50 text-gray-800 font-semibold text-[14px] transition-all"
            data-testid="about-faq-cta"
          >
            See FAQ
          </button>
        </div>
      </main>

      <footer className="text-center py-10 px-6 text-[12px] text-gray-400">
        <a href="https://www.wokelo.ai/legal?tab=privacy" className="hover:text-gray-600 underline underline-offset-2" target="_blank" rel="noopener noreferrer">Privacy</a>
        {" · "}
        <a href="https://www.wokelo.ai/legal" className="hover:text-gray-600 underline underline-offset-2" target="_blank" rel="noopener noreferrer">Terms</a>
        {" · "}
        <span>© 2026 Wokelo AI</span>
      </footer>
    </div>
  );
}
